/**
 * A school's own email setup.
 *
 * Everything on this screen is driven by what the server says the school has
 * been granted. There is no client-side role check: a school administrator who
 * has not been given their own mail server sees an explanation, not a switch
 * that springs back, and a site administrator viewing the same screen through
 * /schools/:id/settings sees exactly what the school sees.
 *
 * The two overrides are independent on purpose. A school may want its mail to
 * come from its own domain while keeping TeachCraftAI's wording, or the
 * reverse - folding them into one switch would force a choice nobody asked for.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import type { SchoolEmailSettings as SchoolEmail } from '../../lib/api/client'
import { Button, Checkbox, Pill, Spinner } from '../../components/ui'
import { Icon } from '../../components/icons'
import { toast } from '../../components/toast'
import { EmailTemplates } from './EmailTemplates'
import { SmtpFields, TestSend, draftFrom, portOf } from './SmtpFields'
import type { SmtpDraft } from './SmtpFields'
import './email.css'

export function SchoolEmailSettings({ schoolId }: { schoolId?: string }) {
  const key = ['school', 'email', schoolId ?? 'current']
  const settings = useQuery({ queryKey: key, queryFn: () => api.schoolEmail(schoolId) })

  if (settings.isLoading) return <Spinner label="Loading email settings" />
  if (!settings.data) return null

  return (
    <div className="tc-email-sections">
      <SchoolSmtpPanel
        key={JSON.stringify(settings.data)}
        settings={settings.data}
        schoolId={schoolId}
        cacheKey={key}
      />
      <SchoolTemplatePanel settings={settings.data} schoolId={schoolId} cacheKey={key} />
      <EmailTemplates schoolId={schoolId} isSchool />
    </div>
  )
}

function SchoolSmtpPanel({
  settings,
  schoolId,
  cacheKey,
}: {
  settings: SchoolEmail
  schoolId?: string
  cacheKey: string[]
}) {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<SmtpDraft | null>(null)
  const [clearPassword, setClearPassword] = useState(false)

  const value = draft ?? draftFrom(settings)
  const set = <K extends keyof SmtpDraft>(k: K, next: SmtpDraft[K]) =>
    setDraft({ ...value, [k]: next })
  const { port, valid: portValid } = portOf(value)
  const dirty = draft !== null || clearPassword

  const write = (updated: SchoolEmail) => queryClient.setQueryData(cacheKey, updated)

  const toggle = useMutation({
    mutationFn: (use: boolean) => api.setSchoolUseCustomSmtp(use, schoolId),
    onSuccess: (updated) => {
      write(updated)
      toast(
        updated.use_custom_smtp
          ? 'Your school now sends through its own mail server.'
          : 'Your school now sends through TeachCraftAI.',
      )
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  const save = useMutation({
    mutationFn: () =>
      api.updateSchoolSmtp(
        {
          smtp_host: value.smtp_host,
          smtp_port: port,
          smtp_username: value.smtp_username,
          smtp_password: value.smtp_password ? value.smtp_password : clearPassword ? '' : undefined,
          smtp_starttls: value.smtp_starttls,
          smtp_from_email: value.smtp_from_email,
          smtp_from_name: value.smtp_from_name,
          smtp_reply_to: value.smtp_reply_to,
        },
        schoolId,
      ),
    onSuccess: (updated) => {
      write(updated)
      setDraft(null)
      setClearPassword(false)
      toast('Mail server saved.')
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  const test = useMutation({
    mutationFn: (to: string) => api.sendSchoolTestEmail(schoolId, to),
    onSuccess: (result) => toast(result.message, result.ok ? 'info' : 'error'),
    onError: (error: Error) => toast(error.message, 'error'),
  })

  return (
    <section className="tc-email-panel" aria-labelledby="school-smtp-title">
      <header className="tc-email-heading">
        <span className="tc-email-icon" aria-hidden="true">
          <Icon.settings size={20} />
        </span>
        <div>
          <span className="tc-email-eyebrow">School policy</span>
          <h2 id="school-smtp-title">Mail server</h2>
          <p>
            Where your school's notifications are sent from. Unless you run your own, they come
            from TeachCraftAI.
          </p>
        </div>
        <SourcePill source={settings.active_source} />
      </header>

      <div className="tc-email-body">
        {!settings.allow_custom_smtp ? (
          <p className="tc-email-locked">
            <Icon.warning size={17} />
            <span>
              Your school's notifications are sent through TeachCraftAI's mail server. If you would
              rather they came from your own domain, ask TeachCraftAI to turn this on.
            </span>
          </p>
        ) : (
          <>
            <Checkbox
              label="Use our own mail server"
              checked={settings.use_custom_smtp}
              disabled={toggle.isPending}
              onChange={(e) => toggle.mutate(e.target.checked)}
            />
            <p className="tc-email-note">
              With this off, your notifications still go out — through TeachCraftAI's server rather
              than yours.
            </p>

            <SmtpFields
              value={value}
              set={set}
              passwordConfigured={settings.smtp_password_configured}
              clearPassword={clearPassword}
              onClearPassword={setClearPassword}
            />

            <div className="tc-email-actions">
              <Button
                variant="primary"
                disabled={!dirty || !portValid || save.isPending}
                onClick={() => save.mutate()}
              >
                {save.isPending ? 'Saving…' : 'Save mail server'}
              </Button>
              <Button
                variant="secondary"
                disabled={!dirty}
                onClick={() => {
                  setDraft(null)
                  setClearPassword(false)
                }}
              >
                Discard changes
              </Button>
            </div>

            <TestSend
              dirty={dirty}
              pending={test.isPending}
              onSend={(to) => test.mutate(to)}
            />
          </>
        )}
      </div>
    </section>
  )
}

function SchoolTemplatePanel({
  settings,
  schoolId,
  cacheKey,
}: {
  settings: SchoolEmail
  schoolId?: string
  cacheKey: string[]
}) {
  const queryClient = useQueryClient()
  const toggle = useMutation({
    mutationFn: (use: boolean) => api.setSchoolUseCustomTemplates(use, schoolId),
    onSuccess: (updated) => {
      queryClient.setQueryData(cacheKey, updated)
      // The notification list below changes shape entirely with this switch,
      // so it is refetched rather than patched.
      queryClient.invalidateQueries({ queryKey: ['school', 'email', schoolId ?? 'current'] })
      toast(
        updated.use_custom_templates
          ? 'Your school now uses its own wording.'
          : 'Your school now uses the TeachCraftAI wording.',
      )
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  if (!settings.allow_custom_templates) {
    return (
      <section className="tc-email-panel">
        <div className="tc-email-body">
          <p className="tc-email-locked">
            <Icon.warning size={17} />
            <span>
              Your school uses TeachCraftAI's notification wording. To write your own, ask
              TeachCraftAI to turn this on.
            </span>
          </p>
        </div>
      </section>
    )
  }

  return (
    <section className="tc-email-panel">
      <div className="tc-email-body">
        <Checkbox
          label="Write our own notification wording"
          checked={settings.use_custom_templates}
          disabled={toggle.isPending}
          onChange={(e) => toggle.mutate(e.target.checked)}
        />
        <p className="tc-email-note">
          With this off, your school sends TeachCraftAI's wording — and picks up any improvements
          to it. Turning it on lets you edit each notification below.
        </p>
      </div>
    </section>
  )
}

function SourcePill({ source }: { source: string }) {
  if (source === 'school') return <Pill tone="success">Sending through your server</Pill>
  if (source === 'none') return <Pill tone="warning">No mail server yet</Pill>
  return <Pill tone="secondary">Sending through TeachCraftAI</Pill>
}
